import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { APICommonService } from '@nationwide/internet-servicing-angular-services';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { LoggerService } from '../../../shared/services/logger.service';
import { TokenDataService } from '../../../shared/services/token-data.service';
import { UtilService } from '../../../shared/services/util.service';
import { ClaimsDataService } from './claims-data.service';
import { CommercialLoggingService } from './commercial-logging.service';

interface TemplateContentItem {
    name: string;
    value: string;
}

@Injectable()
export class OmsService {
    private emailBody: { [x: string]: any } = {};

    constructor(
        private httpClient: HttpClient,
        private apiCommon: APICommonService,
        private tokenDataService: TokenDataService,
        private util: UtilService,
        private LOGGER: LoggerService,
        private commercialLoggingService: CommercialLoggingService,
        private claimsDataService: ClaimsDataService
    ) { }

    buildRequest(emailContents: any, lineOfBusiness: string): any {
        const request = {
            template: {
                templateName: this.getTemplateName(lineOfBusiness),
                templateContent: emailContents
            }
        };
        return request;
    }

    buildTemplateContent(formData: any, namedInsured: string): any {
        this.emailBody = {};
        const lineOfBusiness = this.claimsDataService.getFnolFlow();
        this.mapCommonFields(formData, namedInsured, lineOfBusiness);
        switch (lineOfBusiness) {
            case 'General Liability': {
                this.mapGeneralLiabilityFields(formData);
                break;
            }
            case 'Workers Compensation': {
                this.mapWorkersCompensationFields(formData);
                break;
            }
            case 'Commercial Auto': {
                this.mapCommercialAutoFields(formData);
                break;
            }
            case 'Commercial Property': {
                this.mapCommercialPropertyFields(formData);
                break;
            }
        }
        return this.formatEmailBody();
    }

    buildTemplateForCoi(policyList: any, ecifPolicyNumber: string): any {
        let selectedPolicyNumbers: string;
        for (let index = 0; index < policyList.length; index++) {
            if (index === 0) {
                selectedPolicyNumbers = policyList[index].policyNumber;
            } else {
                selectedPolicyNumbers = `${selectedPolicyNumbers}, ${policyList[index].policyNumber}`;
            }
        }

        const omsDataFromCOI = {
            ecifPolicyNumber
        };
        return omsDataFromCOI;
    }

    getTemplateName(lineOfBusiness: string): string {
        switch (lineOfBusiness) {
            case 'General Liability': {
                return 'CL_FNOL_GL';
            }
            case 'Workers Compensation': {
                return 'CL_FNOL_WC';
            }
            case 'Commercial Auto': {
                return 'CL_FNOL_AUTO';
            }
            case 'Commercial Property': {
                return 'CL_FNOL_PROPERTY';
            }
        }
    }

    sendEmail(emailContents: any, lineOfBusiness: string): Observable<any> {
        const singleEmailUrl = `${environment.API_ENVIRONMENT + environment.CLAIMS_API.INTERNET_SERVICING_CLAIMS}digital-interactions/send-emails`;
        const requestBody = this.buildRequest(emailContents, lineOfBusiness);
        const authToken = this.tokenDataService.getToken();
        const appClientId = environment.appProps.appClientId;
        const sessionId = window.sessionStorage.getItem('sessionId');
        let headers = this.apiCommon.buildHeader({ appClientId, authToken, contentType: 'application/json', sessionId, headerType: 'X-NW-Message-ID' });
        headers = headers.delete('X-NW-SessionID');
        this.commercialLoggingService.logServiceSuccess(
            this.LOGGER,
            'Commercial Claims Service',
            'OmsService',
            'sendEmailRequestBuilt',
            this.getServiceLogMetadata()
        );
        return this.httpClient.post(singleEmailUrl, requestBody, { headers, observe: 'response' })
            .pipe(catchError((error: HttpErrorResponse) => this.util.handleError(`Error sending FNOL OMS request with URL: "${singleEmailUrl}"`, error)));
    }

    private formatAreasOfDamage(damageList: string[]): string {
        let areasOfDamage = '';
        for (const entry of damageList) {
            areasOfDamage = `${areasOfDamage + entry},`;
        }
        areasOfDamage = areasOfDamage.replace(/,\s*$/, '');
        return areasOfDamage;
    }

    private formatEmailBody(): TemplateContentItem[] {
        return Object.entries(this.emailBody).map(([name, value]) => ({ name, value }));
    }

    private formatTimeOfIncident(formData: any): string {
        return `${formData.timeOfIncident.incidentHour}:${formData.timeOfIncident.incidentMinute} ${formData.incidentMeridiem}`;
    }

    private mapCommercialAutoFields(formData: any): void {
        this.emailBody.year = formData.commercialAuto.vehicleYear;
        this.emailBody.make = formData.commercialAuto.vehicleMake;
        this.emailBody.model = formData.commercialAuto.vehicleModel;
        if (formData.commercialAuto.vehicleVIN) {
            this.emailBody.vin = formData.commercialAuto.vehicleVIN;
        }
        this.emailBody.typeOfLoss = formData.commercialAuto.primaryCause;
        this.emailBody.insuredDriver = formData.commercialAuto.insuredDriver;
        this.emailBody.isVehicleDrivable = formData.commercialAuto.vehicleDrivableAnswer;
        this.emailBody.airBagsDeployed = formData.commercialAuto.airBagsDeployedAnswer;
        this.emailBody.carSeatInVehicle = formData.commercialAuto.carSeatAnswer;
        this.emailBody.carSeatInUse = formData.commercialAuto.carSeatUseAnswer;
        this.emailBody.carSeatDamaged = formData.commercialAuto.carSeatDamageAnswer;
        this.emailBody.authoritiesNotified = formData.commercialAuto.authoritiesNotifiedAnswer;
        this.emailBody.numberOfPassengers = formData.commercialAuto.numberOfPassengers;
        this.mapVehicleLocationFields(formData);
    }

    private mapCommercialPropertyFields(formData: any): void {
        this.emailBody.typeOfLoss = formData.commercialProperty.primaryCause;
        this.emailBody.areasOfDamage = this.formatAreasOfDamage(formData.commercialProperty.damagedAreasList);
        this.emailBody.damageExpected = formData.commercialProperty.totalAmountLostAnswer;
    }

    private mapCommonFields(formData: any, namedInsured: string, lineOfBusiness: string): void {
        this.emailBody.typeOfClaim = lineOfBusiness;
        this.emailBody.detailsForPolicy = this.claimsDataService.getClaimsPolicyNumber();
        this.emailBody.insuredName = namedInsured;
        this.emailBody.emailSubject = 'Insurance claim submission';
        this.emailBody.submitterFirstName = formData.submitterFirstName;
        this.emailBody.submitterLastName = formData.submitterLastName;
        this.emailBody.bestNumberToCall = formData.contactPhoneNumber;
        this.emailBody.phoneType = formData.submitterPhoneType;
        this.emailBody.submitterEmail = formData.submitterEmail;
        this.emailBody.dateOfIncident = formData.dateOfIncident;
        this.emailBody.timeOfIncident = this.formatTimeOfIncident(formData);
        this.mapLossLocationFields(formData);
        this.emailBody.description = formData.description;
        if (formData.submitterRecieveText) {
            this.emailBody.submitterRecieveText = formData.submitterRecieveText;
        }
    }

    private mapGeneralLiabilityFields(formData: any): void {
        this.emailBody.typeOfLoss = formData.generalLiability.lossType;
        this.emailBody.claimantPhoneNumber = formData.generalLiability.claimantPhoneNumber;
        this.emailBody.claimantName = formData.generalLiability.claimantName;
        if (formData.generalLiability.liabilitySubtype) {
            this.emailBody.liabilitySubtype = formData.generalLiability.liabilitySubtype;
        }
        if (formData.generalLiability.estimatedDamages) {
            this.emailBody.estimatedDamages = formData.generalLiability.estimatedDamages;
        }
    }

    private mapLossLocationFields(formData: any): void {
        if (formData.addressGroup.country) {
            this.emailBody.lossLocationCountry = formData.addressGroup.country;
        }
        this.emailBody.lossLocationAddress = formData.addressGroup.address;
        this.emailBody.lossLocationCity = formData.addressGroup.city;
        if (formData.addressGroup.state) {
            this.emailBody.lossLocationState = formData.addressGroup.state;
        }
        if (formData.addressGroup.zip) {
            this.emailBody.lossLocationZip = formData.addressGroup.zip;
        }
        if (formData.addressGroup.postalCode) {
            this.emailBody.lossLocationPostalCode = formData.addressGroup.postalCode;
        }
        if (formData.addressGroup.province) {
            this.emailBody.lossLocationProvince = formData.addressGroup.province;
        }
        if (formData.addressGroup.otherStateProvince) {
            this.emailBody.lossLocationOtherStateProvince = formData.addressGroup.otherStateProvince;
        }
    }

    private mapVehicleLocationFields(formData: any): void {
        if (formData.commercialAuto.addressGroup.country) {
            this.emailBody.vehicleLocationCountry = formData.commercialAuto.addressGroup.country;
        }
        this.emailBody.vehicleLocationAddress = formData.commercialAuto.addressGroup.address;
        this.emailBody.vehicleLocationCity = formData.commercialAuto.addressGroup.city;
        if (formData.commercialAuto.addressGroup.state) {
            this.emailBody.vehicleLocationState = formData.commercialAuto.addressGroup.state;
        }
        if (formData.commercialAuto.addressGroup.zip) {
            this.emailBody.vehicleLocationZip = formData.commercialAuto.addressGroup.zip;
        }
        if (formData.commercialAuto.addressGroup.postalCode) {
            this.emailBody.vehicleLocationPostalCode = formData.commercialAuto.addressGroup.postalCode;
        }
        if (formData.commercialAuto.addressGroup.province) {
            this.emailBody.vehicleLocationProvince = formData.commercialAuto.addressGroup.province;
        }
        if (formData.commercialAuto.addressGroup.otherStateProvince) {
            this.emailBody.vehicleLocationOtherStateProvince = formData.commercialAuto.addressGroup.otherStateProvince;
        }
    }

    private mapWorkersCompensationFields(formData: any): void {
        this.emailBody.employeeFirstName = formData.workersCompensation.employeeFirstName;
        this.emailBody.employeeLastName = formData.workersCompensation.employeeLastName;
        this.emailBody.employeePhoneNumber = formData.workersCompensation.employeePhoneNumber;
        if (formData.workersCompensation.employeeEmail) {
            this.emailBody.employeeEmail = formData.workersCompensation.employeeEmail;
        }
        this.emailBody.employeeOccupation = formData.workersCompensation.employeeOccupation;
        this.emailBody.causeOfEmployeeInjury = formData.workersCompensation.causeOfInjury;
        this.emailBody.primaryInjuryType = formData.workersCompensation.typeOfInjury;
        this.emailBody.injurySeverity = formData.workersCompensation.severityOfInjury;
        this.emailBody.bodyPartInjured = formData.workersCompensation.areaOfInjury;
        this.emailBody.detailedInjuryType = formData.workersCompensation.injuryDetail;
        this.emailBody.additionalInjuryDetail = formData.workersCompensation.detailedBodyPart;
        if (formData.workersCompensation.hospitalizedAnswer) {
            this.emailBody.hospitalizedAnswer = formData.workersCompensation.hospitalizedAnswer;
        }
        if (formData.workersCompensation.lostDaysOfWorkAnswer) {
            this.emailBody.loseFourDaysOfWork = formData.workersCompensation.lostDaysOfWorkAnswer;
        }
        if (formData.workersCompensation.notifiedDate) {
            this.emailBody.dateEmployerNotified = formData.workersCompensation.notifiedDate;
        }
        if (formData.workersCompensation.hiredDate) {
            this.emailBody.dateOfHire = formData.workersCompensation.hiredDate;
        }
        if (formData.workersCompensation.timeFromWorkAnswer) {
            this.emailBody.employeeMissWork = formData.workersCompensation.timeFromWorkAnswer;
        }
        if (formData.workersCompensation.returnedDate) {
            this.emailBody.employeeReturnDate = formData.workersCompensation.returnedDate;
        }
    }

    private getServiceLogMetadata(additionalFields: any = {}): any {
        return {
            policyNumber: window.sessionStorage.getItem('claimsSelectedPolicyNumber') || '',
            policyType: window.sessionStorage.getItem('claimsPolicyType') || 'UNKNOWN',
            lineOfBusiness: window.sessionStorage.getItem('fnolFlow') || 'UNKNOWN',
            claimsDisplayCount: 'UNKNOWN',
            alphaState: 'UNKNOWN',
            previousUrl: window.sessionStorage.getItem('previousUrl') || '',
            ...additionalFields
        };
    }
}
